import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Users as UsersIcon, Search, Save, X, KeyRound, UserCheck, UserX, Mail, ExternalLink, Loader2, Send, AlertCircle, UserPlus, Link2, Link2Off, ShieldCheck, Download, RefreshCw, MailOpen, Bell, Clock, Trash2, RotateCcw, Copy, Check, Pencil, ChevronDown, ArrowUp, ArrowDown, ArrowUpDown, EyeOff, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, formatApiError } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';

// Normalize text for accent-insensitive search: lowercases and strips
// diacritics so "veronica" matches "Verónica".
const norm = (s) => (s || '')
  .toString()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

// Forum moderator capabilities (mirror backend FORUM_MOD_KEYS).
const FORUM_MOD_OPTIONS = [
  { key: 'moderate_thread', label: 'Fijar y cerrar hilos', desc: 'Marcar hilos como fijados o cerrarlos a nuevas respuestas.' },
  { key: 'edit_post', label: 'Editar publicaciones de otros', desc: 'Corregir o limpiar mensajes de otros miembros (groserías, etc.).' },
  { key: 'delete_post', label: 'Eliminar publicaciones de otros', desc: 'Borrar respuestas de otros miembros.' },
  { key: 'delete_thread', label: 'Eliminar hilos de otros', desc: 'Borrar temas completos de otros miembros.' },
  { key: 'create_category', label: 'Crear y borrar categorías', desc: 'Administrar las secciones del foro.' },
  { key: 'block_user', label: 'Bloquear usuarios del foro', desc: 'Bloquear a un miembro de forma temporal (días/meses/años) o permanente.' },
];
const ALL_MOD_KEYS = FORUM_MOD_OPTIONS.map((o) => o.key);
function initialMods(u) {
  if (Array.isArray(u.forum_mod_permissions)) return u.forum_mod_permissions;
  // Not configured yet: council/admin effectively had all (legacy). Default to
  // all so an accidental save doesn't strip an existing moderator's powers.
  const legacy = u.role === 'admin' || u.role === 'council'
    || (u.permissions || []).some((p) => p === 'forum' || String(p).startsWith('forum:'));
  return legacy ? [...ALL_MOD_KEYS] : [];
}

const TABS = [
  { id: 'with-account', label: 'Con cuenta' },
  { id: 'orphans',      label: 'Intérpretes sin cuenta' },
  { id: 'trash',        label: 'Papelera' },
];

// Predicates mirror the invitation stat cards so a click filters the orphan
// table in-place (keeping Excel / send-invite / selection available).
const ORPHAN_FILTER_FNS = {
  sent: (inv) => !!inv,
  opened: (inv) => !!(inv && inv.opened_at),
  activated: (inv) => !!(inv && inv.accepted_at),
  reminded: (inv, item) => !!(inv && inv.reminder_sent_at) && !item.has_account,
  pending: (inv, item) => !!(inv && !inv.accepted_at && (inv.expires_at || '') >= new Date().toISOString()) && !item.has_account,
  no_read: (inv, item) => !!(inv && !inv.accepted_at && !inv.opened_at) && !item.has_account,
  no_invite: (inv) => !inv,
};
const FILTER_LABELS = { sent: 'Enviadas', opened: 'Abiertas', activated: 'Activadas', reminded: 'Recordadas', pending: 'Pendientes', no_read: 'No leídas', no_invite: 'Sin invitar' };

export default function AdminCommunityUsersPage() {
  const [tab, setTab] = useState('with-account');
  const [withAccount, setWithAccount] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [trash, setTrash] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);
  const [selectedOrphans, setSelectedOrphans] = useState(new Set());
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteStats, setInviteStats] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [inviteFilter, setInviteFilter] = useState(null);
  const [orphanCity, setOrphanCity] = useState('');
  const [orphanCat, setOrphanCat] = useState('');
  const [hidden, setHidden] = useState([]);
  const [showHidden, setShowHidden] = useState(false);

  // Sets of hidden identifiers for fast exclusion from lists & stats.
  const hiddenSlugs = useMemo(() => new Set((hidden || []).map((h) => h.slug).filter(Boolean)), [hidden]);
  const hiddenUserIds = useMemo(() => new Set((hidden || []).map((h) => h.user_id).filter(Boolean)), [hidden]);
  const isHidden = (row) => (row?.slug && hiddenSlugs.has(row.slug)) || (row?.id && hiddenUserIds.has(row.id)) || (row?.interpreter_slug && hiddenSlugs.has(row.interpreter_slug));

  async function hideEntry(row) {
    if (!window.confirm(`Ocultar a "${row.full_name || row.slug}" de las estadísticas y listas? No se borra nada; podrás restaurarlo desde "Ver ocultos". La tarjeta "Enviadas" no cambia.`)) return;
    try {
      await api.post('/admin/community-users/hide', {
        slug: row.slug || row.interpreter_slug || null,
        user_id: row.id || null,
        full_name: row.full_name || '',
        email: row.email || '',
      });
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'No se pudo ocultar');
    }
  }
  async function unhideEntry(h) {
    try {
      await api.post('/admin/community-users/unhide', { slug: h.slug || null, user_id: h.user_id || null });
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'No se pudo restaurar');
    }
  }

  async function sendReminders() {
    if (!window.confirm('Enviar recordatorio a las invitaciones de más de 7 días que aún no se activan?')) return;
    setSendingReminders(true);
    try {
      const res = await api.post('/admin/interpreters/send-reminders');
      setInviteResult({ summary: { total: res.data.sent, sent: res.data.sent, skipped: 0, failed: 0 }, results: [], _reminder: true });
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error al enviar recordatorios');
    } finally { setSendingReminders(false); }
  }

  async function sendRemindersNow() {
    if (!window.confirm('Enviar AHORA un recordatorio a TODAS las invitaciones vigentes que aún no se activan (sin esperar los 7 días)?')) return;
    setSendingReminders(true);
    try {
      const res = await api.post('/admin/interpreters/send-reminders-now');
      setInviteResult({ summary: { total: res.data.sent, sent: res.data.sent, skipped: 0, failed: 0 }, results: [], _reminder: true });
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error al enviar recordatorios');
    } finally { setSendingReminders(false); }
  }

  async function sendRemindersUnread() {
    if (!window.confirm('Reenviar la invitación SOLO a quienes NO han abierto el correo (No leído)?')) return;
    setSendingReminders(true);
    try {
      const res = await api.post('/admin/interpreters/send-reminders-unread');
      setInviteResult({ summary: { total: res.data.sent, sent: res.data.sent, skipped: 0, failed: 0 }, results: [], _reminder: true });
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error al enviar recordatorios');
    } finally { setSendingReminders(false); }
  }

  async function load() {
    setLoading(true); setError('');
    try {
      const [w, o, s, t, inv] = await Promise.all([
        api.get('/admin/community-users'),
        api.get('/admin/community-users/orphans'),
        api.get('/admin/interpreter-invitations/stats').catch(() => ({ data: null })),
        api.get('/admin/community-users/trash').catch(() => ({ data: [] })),
        api.get('/admin/interpreter-invitations').catch(() => ({ data: [] })),
      ]);
      const h = await api.get('/admin/community-users/hidden').catch(() => ({ data: [] }));
      setWithAccount(w.data || []);
      setOrphans(o.data || []);
      setInviteStats(s.data);
      setTrash(t.data || []);
      setInvitations(inv.data || []);
      setHidden(h.data || []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const invMap = useMemo(() => {
    const m = {};
    for (const inv of invitations) { // sorted desc by created_at (newest first)
      const slug = inv.interpreter_slug;
      if (!slug) continue;
      if (!m[slug]) {
        m[slug] = { ...inv }; // newest invitation as base (token, dates)
        m[slug].reminder_count = inv.reminder_count || 0;
      } else {
        // Aggregate engagement flags across ALL invitations for this slug so a
        // person counts as opened/reminded/activated if ANY of their invites was.
        const e = m[slug];
        e.opened_at = e.opened_at || inv.opened_at;
        e.accepted_at = e.accepted_at || inv.accepted_at;
        e.reminder_count = (e.reminder_count || 0) + (inv.reminder_count || 0);
        // keep the most recent reminder date
        if ((inv.reminder_sent_at || '') > (e.reminder_sent_at || '')) e.reminder_sent_at = inv.reminder_sent_at;
        else e.reminder_sent_at = e.reminder_sent_at || inv.reminder_sent_at;
      }
    }
    return m;
  }, [invitations]);

  const visibleWithAccount = useMemo(() => {
    const q = norm(query.trim());
    const base = withAccount.filter((u) => !isHidden(u));
    if (!q) return base;
    return base.filter((u) => (
      norm(u.full_name).includes(q) ||
      norm(u.email).includes(q) ||
      norm(u.username).includes(q)
    ));
  }, [withAccount, query, hiddenSlugs, hiddenUserIds]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Full list of every interpreter that has an invitation (orphans + those who
  // already activated their account). Used when an invitation-status filter is
  // active so the filtered count matches the stat cards.
  const allInvited = useMemo(() => {
    const orphanBySlug = {};
    for (const o of orphans) orphanBySlug[o.slug] = o;
    const seen = new Set();
    const list = [];
    for (const inv of invitations) {
      const slug = inv.interpreter_slug;
      if (!slug || seen.has(slug)) continue;
      seen.add(slug);
      const o = orphanBySlug[slug];
      list.push({
        slug,
        full_name: (o && o.full_name) || inv.interpreter_name || slug,
        email: inv.email || (o && o.email) || '',
        city: (o && o.city) || '',
        membership_status: (o && o.membership_status) || '',
        has_account: !o,
      });
    }
    return list;
  }, [invitations, orphans]);

  const visibleOrphans = useMemo(() => {
    const q = norm(query.trim());
    // When a stat-card filter is active, search across ALL invited interpreters
    // (including those who already created an account), not just orphans.
    // "Sin invitar" (no_invite) is the exception: those interpreters have no
    // invitation, so they only exist in the orphans list.
    let list = (inviteFilter && inviteFilter !== 'no_invite') ? allInvited : orphans;
    if (inviteFilter && ORPHAN_FILTER_FNS[inviteFilter]) {
      const fn = ORPHAN_FILTER_FNS[inviteFilter];
      list = list.filter((i) => fn(invMap[i.slug], i));
    }
    if (q) {
      list = list.filter((i) => (
        norm(i.full_name).includes(q) ||
        norm(i.email).includes(q) ||
        norm(i.city).includes(q)
      ));
    }
    if (orphanCity) list = list.filter((i) => (i.city || '') === orphanCity);
    if (orphanCat) list = list.filter((i) => (i.membership_status || '') === orphanCat);
    // Always hide "ocultos" from the working list (they live in the Ocultos panel).
    list = list.filter((i) => !hiddenSlugs.has(i.slug));
    return list;
  }, [orphans, allInvited, query, inviteFilter, invMap, orphanCity, orphanCat, hiddenSlugs]);

  // Distinct values for the orphan table's column filters (Ciudad / Categoría).
  const orphanCityOptions = useMemo(
    () => Array.from(new Set((orphans || []).map((o) => o.city).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [orphans],
  );
  const orphanCatOptions = useMemo(
    () => Array.from(new Set((orphans || []).map((o) => o.membership_status).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [orphans],
  );

  // Card counts are derived from the SAME filter logic that builds the list,
  // so every stat card matches exactly what you see when you click it.
  const cardCounts = useMemo(() => {
    const count = (key, base, excludeHidden) => base.filter(
      (i) => ORPHAN_FILTER_FNS[key](invMap[i.slug], i) && (!excludeHidden || !hiddenSlugs.has(i.slug)),
    ).length;
    return {
      sent: count('sent', allInvited, false),
      opened: count('opened', allInvited, true),
      activated: count('activated', allInvited, true),
      reminded: count('reminded', allInvited, true),
      pending: count('pending', allInvited, true),
      no_read: count('no_read', allInvited, true),
      no_invite: count('no_invite', orphans, true),
    };
  }, [allInvited, orphans, invMap, hiddenSlugs]);


  function toggleOrphan(slug) {
    setSelectedOrphans((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  }
  function selectAllOrphans(checked) {
    setSelectedOrphans(checked ? new Set(visibleOrphans.filter((i) => !i.has_account).map((i) => i.slug)) : new Set());
  }

  async function bulkInvite() {
    if (selectedOrphans.size === 0) return;
    if (!window.confirm(`Enviar ${selectedOrphans.size} invitación(es) de cuenta? Cada intérprete recibirá un correo con un link para registrarse.`)) return;
    setInviting(true); setInviteResult(null);
    try {
      const res = await api.post('/admin/interpreters/bulk-invite', { slugs: Array.from(selectedOrphans) });
      setInviteResult(res.data);
      setSelectedOrphans(new Set());
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error al invitar');
    } finally { setInviting(false); }
  }

  async function bulkRemind() {
    if (selectedOrphans.size === 0) return;
    if (!window.confirm(`Enviar recordatorio a ${selectedOrphans.size} intérprete(s) seleccionado(s)? Si su invitación expiró, se generará una nueva automáticamente.`)) return;
    setInviting(true); setInviteResult(null);
    try {
      const res = await api.post('/admin/interpreters/bulk-remind', { slugs: Array.from(selectedOrphans) });
      setInviteResult({ ...res.data, _reminder: true });
      setSelectedOrphans(new Set());
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error al enviar recordatorios');
    } finally { setInviting(false); }
  }

  async function downloadOrphansExcel(selectedOnly) {
    try {
      const slugs = selectedOnly ? Array.from(selectedOrphans).join(',') : '';
      const res = await api.get('/admin/community-users/orphans/export', { params: { slugs }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `cmic_interpretes_sin_cuenta_${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('No se pudo generar el Excel.');
    }
  }

  async function downloadScopedExcel(scope) {
    try {
      const res = await api.get('/admin/community-users/export', { params: { scope }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `cmic_usuarios_${scope}_${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('No se pudo generar el Excel.');
    }
  }

  async function restoreFromTrash(entry) {
    if (!window.confirm(`¿Restaurar a "${entry.label}"${entry.had_interpreter ? ' y su perfil de intérprete' : ''}? Volverá a aparecer en el sitio.`)) return;
    try {
      await api.post(`/admin/community-users/trash/${entry.id}/restore`);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'No se pudo restaurar.');
    }
  }

  async function purgeFromTrash(entry) {
    if (!window.confirm(`Eliminar DEFINITIVAMENTE a "${entry.label}". Esta acción NO se puede deshacer. ¿Continuar?`)) return;
    try {
      await api.delete(`/admin/community-users/trash/${entry.id}`);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'No se pudo eliminar.');
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-community-users-page">
      <div>
        <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2">
          <UsersIcon size={22} className="text-cmic-600" /> Usuarios
        </h1>
        <p className="text-charcoal-500 text-sm mt-1">
          Cuentas de portal (típicamente intérpretes). Edita correo, nombre, contraseña y accesos. Cualquier cambio en nombre o correo se sincroniza automáticamente con el directorio de intérpretes.
        </p>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm flex items-center gap-2"><AlertCircle size={15} /> {error}</div>}
      {inviteResult && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg p-3 text-sm" data-testid="invite-result">
          {(() => {
            const s = inviteResult.summary || inviteResult;
            const n = s.sent || 0;
            const failed = s.failed || 0;
            const skipped = s.skipped || 0;
            const main = inviteResult._reminder
              ? `✓ ${n} recordatorio(s) enviado(s).`
              : `✓ ${n} invitación(es) enviada(s).`;
            const extra = [
              failed > 0 ? `${failed} fallaron.` : '',
              (!inviteResult._reminder && skipped > 0) ? `${skipped} omitida(s) (ya tienen cuenta o sin correo).` : '',
            ].filter(Boolean).join(' ');
            return `${main}${extra ? ' ' + extra : ''}`;
          })()}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200">
        <div className="flex gap-1">
          {TABS.map((tt) => (
            <button key={tt.id} onClick={() => { setTab(tt.id); setQuery(''); load(); }}
              data-testid={`community-tab-${tt.id}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === tt.id ? 'border-cmic-600 text-cmic-700' : 'border-transparent text-charcoal-500 hover:text-charcoal-800'}`}>
              {tt.label}
              <span className="ml-1.5 text-[10px] bg-gray-100 rounded-full px-1.5 py-0.5">
                {tt.id === 'with-account' ? withAccount.filter((u) => !isHidden(u)).length : tt.id === 'orphans' ? orphans.filter((o) => !hiddenSlugs.has(o.slug)).length : trash.length}
              </span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <ExcelExportMenu onDownload={downloadScopedExcel} />
        </div>
      </div>

      {tab === 'orphans' && (
        <button onClick={load} data-testid="community-refresh" className="inline-flex items-center gap-1.5 text-xs font-medium text-cmic-600 hover:text-cmic-700">
          <RefreshCw size={13} /> Actualizar lista
        </button>
      )}

      {tab === 'orphans' && inviteStats && (
        <InvitationStatsBoard
          stats={{ ...inviteStats, ...cardCounts }}
          invitations={invitations}
          onSendReminders={sendReminders}
          onSendRemindersNow={sendRemindersNow}
          onSendRemindersUnread={sendRemindersUnread}
          sending={sendingReminders}
          activeFilter={inviteFilter}
          onFilter={(key) => setInviteFilter((prev) => (prev === key ? null : key))}
        />
      )}

      {/* Search bar — placed right above the list for easy reach. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />
          <input type="text" placeholder="Buscar por nombre, correo o usuario…" value={query} onChange={(e) => setQuery(e.target.value)}
            data-testid="community-search"
            className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
        </div>
        {tab === 'orphans' && (
          <>
            <select value={orphanCat} onChange={(e) => setOrphanCat(e.target.value)}
              data-testid="orphans-filter-category"
              className="py-2.5 px-3 border border-gray-200 rounded-xl text-sm bg-white text-charcoal-700 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 capitalize">
              <option value="">Categoría: todas</option>
              {orphanCatOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={orphanCity} onChange={(e) => setOrphanCity(e.target.value)}
              data-testid="orphans-filter-city"
              className="py-2.5 px-3 border border-gray-200 rounded-xl text-sm bg-white text-charcoal-700 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500">
              <option value="">Ciudad: todas</option>
              {orphanCityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {(orphanCat || orphanCity) && (
              <button type="button" onClick={() => { setOrphanCat(''); setOrphanCity(''); }}
                data-testid="orphans-filter-clear"
                className="inline-flex items-center gap-1 text-xs font-medium text-cmic-700 hover:text-cmic-900 bg-white border border-cmic-200 rounded-lg px-3 py-2.5">
                <X size={12} /> Limpiar filtros
              </button>
            )}
          </>
        )}
      </div>

      {loading ? (
        <div className="py-24 flex items-center justify-center"><Loader2 className="animate-spin text-cmic-500" size={32} /></div>
      ) : tab === 'with-account' ? (
        <WithAccountTable rows={visibleWithAccount} onEdit={setEditing} onHide={hideEntry} />
      ) : tab === 'trash' ? (
        <TrashTable rows={trash} onRestore={restoreFromTrash} onPurge={purgeFromTrash} />
      ) : (
        <OrphansTable
          rows={visibleOrphans}
          invMap={invMap}
          selected={selectedOrphans}
          onToggle={toggleOrphan}
          onSelectAll={selectAllOrphans}
          onBulkInvite={bulkInvite}
          onBulkRemind={bulkRemind}
          onHide={hideEntry}
          inviting={inviting}
          onDownloadAll={() => downloadOrphansExcel(false)}
          onDownloadSelected={() => downloadOrphansExcel(true)}
          activeFilter={inviteFilter}
          onClearFilter={() => setInviteFilter(null)}
        />
      )}

      {!loading && (tab === 'with-account' || tab === 'orphans') && (
        <HiddenPanel
          hidden={hidden}
          open={showHidden}
          onToggle={() => setShowHidden((v) => !v)}
          onRestore={unhideEntry}
        />
      )}

      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
          onDeleted={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function HiddenPanel({ hidden, open, onToggle, onRestore }) {
  const count = (hidden || []).length;
  return (
    <div className="mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="hidden-panel">
      <button type="button" onClick={onToggle} data-testid="hidden-panel-toggle"
        className="w-full flex items-center justify-between gap-2 px-5 py-3 text-sm font-medium text-charcoal-600 hover:bg-gray-50">
        <span className="inline-flex items-center gap-2">
          <EyeOff size={15} className="text-charcoal-400" />
          Ocultos de estadísticas ({count})
        </span>
        <ChevronDown size={16} className={`text-charcoal-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="border-t border-gray-100 p-2">
          {count === 0 ? (
            <p className="text-sm text-charcoal-500 text-center py-6">No hay registros ocultos. Los intérpretes/cuentas que ocultes aparecerán aquí para restaurarlos.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {hidden.map((h) => (
                <li key={h.id} className="flex items-center gap-3 px-3 py-2.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-charcoal-800 truncate">{h.full_name || h.slug || h.user_id}</p>
                    <p className="text-[11px] text-charcoal-400 truncate">{h.email || h.slug}{h.hidden_by ? ` · ocultado por ${h.hidden_by}` : ''}</p>
                  </div>
                  <button onClick={() => onRestore(h)} data-testid={`hidden-restore-${h.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-cmic-700 border border-cmic-200 rounded-md px-2.5 py-1.5 hover:bg-cmic-50">
                    <Eye size={13} /> Restaurar
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function WithAccountTable({ rows, onEdit, onHide }) {
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const sortVal = (u, key) => {
    switch (key) {
      case 'user': return (u.full_name || u.username || '').toLowerCase();
      case 'email': return (u.email || '').toLowerCase();
      case 'status': return u.is_active === false ? 1 : 0;
      case 'profile': return (u.interpreter?.full_name || '').toLowerCase();
      default: return '';
    }
  };
  const sortedRows = useMemo(() => {
    if (!sort.key) return rows;
    const arr = [...rows];
    arr.sort((a, b) => {
      const va = sortVal(a, sort.key); const vb = sortVal(b, sort.key);
      if (va < vb) return sort.dir === 'asc' ? -1 : 1;
      if (va > vb) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [rows, sort]);

  const SortTh = ({ label, k, align = 'left' }) => (
    <th className={`${align === 'center' ? 'text-center' : 'text-left'} px-3 py-3 font-medium text-charcoal-600`}>
      <button type="button" onClick={() => toggleSort(k)} data-testid={`with-account-sort-${k}`}
        className={`inline-flex items-center gap-1 hover:text-cmic-600 transition-colors ${align === 'center' ? 'mx-auto' : ''}`}>
        {label}
        {sort.key === k
          ? (sort.dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)
          : <ArrowUpDown size={12} className="text-charcoal-300" />}
      </button>
    </th>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-right px-2 py-3 font-medium text-charcoal-400 text-[10px] w-10">#</th>
              <SortTh label="Usuario" k="user" />
              <SortTh label="Correo" k="email" />
              <SortTh label="Estado" k="status" align="center" />
              <th className="text-center px-3 py-3 font-medium text-charcoal-600">Accesos</th>
              <SortTh label="Perfil intérprete" k="profile" />
              <th className="text-right px-3 py-3 font-medium text-charcoal-600">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedRows.map((u, idx) => (
              <tr key={u.id} data-testid={`community-user-row-${u.id}`} className="hover:bg-gray-50/60">
                <td className="px-2 py-3 text-right text-charcoal-400 text-xs tabular-nums">{idx + 1}</td>
                <td className="px-3 py-3">
                  <div className="font-medium text-charcoal-800 flex items-center gap-1.5 flex-wrap">
                    {u.full_name || '—'}
                    {u.is_council && (
                      <span data-testid={`community-council-badge-${u.id}`}
                        title={u.council_role_name ? `Consejo Directivo — ${u.council_role_name}` : 'Miembro del Consejo Directivo'}
                        className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">
                        <ShieldCheck size={10} /> {u.council_role_name || 'Consejo'}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-charcoal-500">@{u.username}</div>
                </td>
                <td className="px-3 py-3 text-charcoal-700">{u.email || <span className="text-charcoal-400 italic">sin correo</span>}</td>
                <td className="px-3 py-3 text-center">
                  {u.is_active === false ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200 px-2 py-0.5 rounded">DESHABILITADO</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded">ACTIVO</span>
                  )}
                </td>
                <td className="px-3 py-3 text-center text-[10px] space-x-1">
                  <span title="Acceso al área Interna (Foro)" className={`px-1.5 py-0.5 rounded font-semibold ${u.forum_access !== false ? 'bg-cmic-50 text-cmic-700' : 'bg-gray-100 text-gray-400 line-through'}`}>INT</span>
                  <span title="Acceso a Eventos internos" className={`px-1.5 py-0.5 rounded font-semibold ${u.events_access !== false ? 'bg-cmic-50 text-cmic-700' : 'bg-gray-100 text-gray-400 line-through'}`}>EVT</span>
                </td>
                <td className="px-3 py-3 text-[12px]">
                  {u.interpreter ? (
                    <div className="flex flex-col gap-1.5">
                      <Link to={`/admin/interpreters?slug=${u.interpreter.slug}`} data-testid={`community-profile-edit-${u.id}`}
                        className="text-cmic-600 hover:underline inline-flex items-center gap-1 font-medium">
                        <Pencil size={11} /> {u.interpreter.full_name}
                      </Link>
                      <a href={`/es/directorio/${u.interpreter.slug}`} target="_blank" rel="noopener noreferrer"
                        data-testid={`community-profile-public-${u.id}`}
                        className="text-charcoal-500 hover:text-cmic-600 hover:underline inline-flex items-center gap-1 text-[11px]">
                        <ExternalLink size={11} /> Ver perfil público
                      </a>
                    </div>
                  ) : (
                    <span className="text-charcoal-400 italic">sin vincular</span>
                  )}
                </td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  <button onClick={() => onEdit(u)} data-testid={`community-edit-${u.id}`}
                    className="text-xs text-cmic-600 hover:bg-cmic-50 rounded px-2 py-1 font-medium">
                    Editar
                  </button>
                  <button onClick={() => onHide(u)} data-testid={`community-hide-${u.id}`}
                    title="Ocultar de estadísticas y listas (no se borra; se puede restaurar)"
                    className="text-xs text-charcoal-400 hover:text-rose-600 hover:bg-rose-50 rounded px-2 py-1 font-medium inline-flex items-center gap-1">
                    <EyeOff size={12} /> Ocultar
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-10 text-center text-charcoal-500 text-sm">No hay usuarios.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TrashTable({ rows, onRestore, onPurge }) {
  return (
    <div data-testid="community-trash-table">
      <p className="text-xs text-charcoal-500 max-w-2xl mb-3">
        Usuarios eliminados. No aparecen en ninguna parte del sitio (directorio, búsqueda, inicio de sesión). Puedes <strong>restaurarlos</strong> para que vuelvan tal cual, o <strong>eliminarlos definitivamente</strong>.
      </p>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-right px-2 py-3 font-medium text-charcoal-400 text-[10px] w-10">#</th>
                <th className="text-left px-3 py-3 font-medium text-charcoal-600">Usuario</th>
                <th className="text-left px-3 py-3 font-medium text-charcoal-600">Correo</th>
                <th className="text-center px-3 py-3 font-medium text-charcoal-600">Perfil intérprete</th>
                <th className="text-left px-3 py-3 font-medium text-charcoal-600">Eliminado</th>
                <th className="text-right px-3 py-3 font-medium text-charcoal-600">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r, idx) => (
                <tr key={r.id} data-testid={`trash-row-${r.id}`} className="hover:bg-gray-50/60">
                  <td className="px-2 py-3 text-right text-charcoal-400 text-xs tabular-nums">{idx + 1}</td>
                  <td className="px-3 py-3">
                    <div className="font-medium text-charcoal-800">{r.label || '—'}</div>
                    {r.username && <div className="text-[11px] text-charcoal-500">@{r.username}</div>}
                  </td>
                  <td className="px-3 py-3 text-charcoal-700">{r.email || <span className="text-charcoal-400 italic">sin correo</span>}</td>
                  <td className="px-3 py-3 text-center text-[12px]">
                    {r.had_interpreter ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded">{r.interpreter_name || 'INCLUIDO'}</span>
                    ) : (
                      <span className="text-charcoal-400 italic">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-[11px] text-charcoal-500">
                    {r.deleted_at ? new Date(r.deleted_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    {r.deleted_by && <div className="text-[10px] text-charcoal-400">por {r.deleted_by}</div>}
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <button onClick={() => onRestore(r)} data-testid={`trash-restore-${r.id}`}
                      className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:bg-emerald-50 rounded px-2 py-1 font-medium">
                      <RotateCcw size={13} /> Restaurar
                    </button>
                    <button onClick={() => onPurge(r)} data-testid={`trash-purge-${r.id}`}
                      className="inline-flex items-center gap-1 text-xs text-rose-600 hover:bg-rose-50 rounded px-2 py-1 font-medium ml-1">
                      <Trash2 size={13} /> Eliminar
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan="6" className="px-4 py-10 text-center text-charcoal-500 text-sm">La papelera está vacía.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const inviteLink = (token) => `${window.location.origin}/invitacion/${token}`;

function getInviteStatus(inv) {
  if (!inv) return { key: 'none', label: 'No enviada', cls: 'bg-gray-100 text-gray-500 border-gray-200' };
  const now = new Date().toISOString();
  if (inv.accepted_at) return { key: 'activated', label: 'Activada', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  if ((inv.expires_at || '') < now) return { key: 'expired', label: 'Expirada', cls: 'bg-rose-50 text-rose-600 border-rose-200' };
  if (inv.reminder_sent_at) return { key: 'reminded', label: 'Recordada', cls: 'bg-amber-50 text-amber-700 border-amber-200' };
  if (inv.opened_at) return { key: 'opened', label: 'Abierta', cls: 'bg-sky-50 text-sky-700 border-sky-200' };
  return { key: 'sent', label: 'Enviada', cls: 'bg-cmic-50 text-cmic-700 border-cmic-200' };
}

function CopyLinkButton({ token, testid, label = 'Copiar enlace' }) {
  const [copied, setCopied] = useState(false);
  return (
    <button type="button" data-testid={testid}
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(inviteLink(token)).then(() => {
          setCopied(true); setTimeout(() => setCopied(false), 1600);
        });
      }}
      className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-md px-2 py-1 border transition-colors ${copied ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-cmic-700 border-cmic-200 hover:bg-cmic-50'}`}>
      {copied ? <><Check size={12} /> Copiado</> : <><Copy size={12} /> {label}</>}
    </button>
  );
}

function InvitationStatsBoard({ stats, invitations, onSendReminders, onSendRemindersNow, onSendRemindersUnread, sending, activeFilter, onFilter }) {
  const cards = [
    { key: 'sent', label: 'Enviadas', value: stats.sent, Icon: Send, color: 'text-cmic-600', bg: 'bg-cmic-50' },
    { key: 'opened', label: 'Abiertas', value: stats.opened, Icon: MailOpen, color: 'text-sky-600', bg: 'bg-sky-50' },
    { key: 'no_read', label: 'No leídas', value: stats.no_read, Icon: Mail, color: 'text-orange-600', bg: 'bg-orange-50' },
    { key: 'activated', label: 'Activadas', value: stats.activated, Icon: UserCheck, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { key: 'reminded', label: 'Recordadas', value: stats.reminded, Icon: Bell, color: 'text-amber-600', bg: 'bg-amber-50' },
    { key: 'pending', label: 'Pendientes', value: stats.pending, Icon: Clock, color: 'text-violet-600', bg: 'bg-violet-50' },
    { key: 'no_invite', label: 'Sin invitar', value: stats.no_invite, Icon: UserX, color: 'text-rose-600', bg: 'bg-rose-50' },
  ];
  // How many emails each reminder button will send (mirrors the backend queries).
  const now = new Date().toISOString();
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString();
  let cntUnread = 0, cntNow = 0, cntPlus7 = 0;
  for (const inv of (invitations || [])) {
    const valid = !inv.accepted_at && (inv.expires_at || '') > now;
    if (!valid) continue;
    cntNow += 1;
    if (!inv.opened_at) cntUnread += 1;
    if (!inv.reminder_sent_at && (inv.created_at || '') <= weekAgo) cntPlus7 += 1;
  }
  return (
    <div className="mb-5 bg-white rounded-2xl border border-gray-100 shadow-sm p-5" data-testid="invitation-stats-board">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="font-semibold text-charcoal-800 text-sm">Estado de invitaciones</h3>
          <p className="text-xs text-charcoal-500">Tasa de activación: <strong className="text-emerald-600">{stats.activation_rate}%</strong>
            {stats.opened_not_activated > 0 && <> · {stats.opened_not_activated} abrieron pero no activaron</>}
            <span className="ml-1 text-charcoal-400">· toca una tarjeta para filtrar la lista de abajo</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={onSendRemindersUnread} disabled={sending || cntUnread === 0} data-testid="send-reminders-unread-btn"
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-charcoal-200 text-charcoal-600 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <MailOpen size={14} />}
            Recordar a no leídos ({cntUnread})
          </button>
          <button onClick={onSendRemindersNow} disabled={sending || cntNow === 0} data-testid="send-reminders-now-btn"
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-amber-300 text-amber-700 text-sm font-medium rounded-lg hover:bg-amber-50 disabled:opacity-50">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />}
            Recordar ahora ({cntNow})
          </button>
          <button onClick={onSendReminders} disabled={sending || cntPlus7 === 0} data-testid="send-reminders-btn"
            className="inline-flex items-center gap-2 px-3 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />}
            Enviar recordatorios +7 días ({cntPlus7})
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        {cards.map(({ key, label, value, Icon, color, bg }) => {
          const isActive = activeFilter === key;
          return (
            <button key={key} type="button" onClick={() => onFilter(key)} data-testid={`invite-stat-${key}`}
              aria-pressed={isActive}
              className={`text-left rounded-xl border p-3 flex items-center gap-3 transition-all ${isActive ? 'border-cmic-400 ring-2 ring-cmic-100 bg-cmic-50/40' : 'border-gray-100 hover:border-cmic-300 hover:shadow-sm hover:bg-gray-50/60'}`}>
              <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}><Icon size={16} className={color} /></div>
              <div>
                <div className="text-xl font-bold text-charcoal-800 leading-none">{value}</div>
                <div className="text-[11px] text-charcoal-500 mt-0.5">{label}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const EXCEL_SCOPES = [
  { scope: 'all', label: 'Todos', hint: 'Con cuenta + sin cuenta' },
  { scope: 'active', label: 'Usuarios activos', hint: 'Cuentas habilitadas' },
  { scope: 'pending', label: 'Usuarios pendientes', hint: 'Esperan aprobación del consejo' },
  { scope: 'invited', label: 'Invitación enviada + enlace', hint: 'Incluye el enlace único' },
];

function ExcelExportMenu({ onDownload }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="excel-export-menu-btn"
        className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-cmic-200 text-cmic-700 text-sm font-medium rounded-lg hover:bg-cmic-50 whitespace-nowrap">
        <Download size={14} /> Descargar Excel <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div data-testid="excel-export-menu" className="absolute left-0 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50">
          <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 text-[11px] font-semibold text-charcoal-500 uppercase tracking-wide">¿Qué quieres descargar?</div>
          {EXCEL_SCOPES.map(({ scope, label, hint }) => (
            <button key={scope} type="button" data-testid={`excel-export-${scope}`}
              onClick={() => { onDownload(scope); setOpen(false); }}
              className="w-full text-left px-4 py-2.5 hover:bg-cmic-50 border-b border-gray-50 last:border-b-0 transition-colors">
              <div className="text-sm font-medium text-charcoal-800">{label}</div>
              <div className="text-[11px] text-charcoal-500">{hint}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function OrphansTable({ rows, invMap, selected, onToggle, onSelectAll, onBulkInvite, onBulkRemind, onHide, inviting, onDownloadAll, onDownloadSelected, activeFilter, onClearFilter }) {
  const selectable = rows.filter((r) => !r.has_account);
  const allChecked = selectable.length > 0 && selectable.every((r) => selected.has(r.slug));
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  const invRank = (inv) => (!inv ? 0 : inv.accepted_at ? 4 : inv.opened_at ? 3 : inv.reminder_sent_at ? 2 : 1);
  const sortVal = (i, key) => {
    const inv = invMap[i.slug];
    switch (key) {
      case 'name': return norm(i.full_name);
      case 'email': return norm(i.email);
      case 'invitation': return invRank(inv);
      case 'opened': return inv?.opened_at || '';
      case 'category': return norm(i.membership_status);
      case 'city': return norm(i.city);
      default: return '';
    }
  };
  const sortedRows = useMemo(() => {
    if (!sort.key) return rows;
    const arr = [...rows];
    arr.sort((a, b) => {
      const va = sortVal(a, sort.key); const vb = sortVal(b, sort.key);
      if (va < vb) return sort.dir === 'asc' ? -1 : 1;
      if (va > vb) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [rows, sort, invMap]);  // eslint-disable-line react-hooks/exhaustive-deps
  const SortTh = ({ label, k }) => (
    <th className="text-left px-3 py-3 font-medium text-charcoal-600">
      <button type="button" onClick={() => toggleSort(k)} data-testid={`orphans-sort-${k}`}
        className="inline-flex items-center gap-1 hover:text-cmic-700 transition-colors">
        {label}
        {sort.key === k
          ? (sort.dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)
          : <ArrowUpDown size={12} className="text-charcoal-300" />}
      </button>
    </th>
  );
  return (
    <div>
      {activeFilter && (
        <div data-testid="orphans-active-filter" className="mb-3 flex items-center gap-2 bg-cmic-50 border border-cmic-200 text-cmic-800 rounded-lg px-3 py-2 text-sm">
          <span>Filtrando por: <strong>{FILTER_LABELS[activeFilter] || activeFilter}</strong> · {rows.length} resultado(s){activeFilter !== 'pending' && <span className="text-cmic-600"> (incluye intérpretes que ya tienen cuenta)</span>}</span>
          <button type="button" onClick={onClearFilter} data-testid="orphans-clear-filter"
            className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-cmic-700 hover:text-cmic-900 bg-white border border-cmic-200 rounded-md px-2 py-1">
            <X size={12} /> Quitar filtro
          </button>
        </div>
      )}
      <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-charcoal-500 max-w-xl">
          {activeFilter
            ? `${rows.length} intérprete(s) con invitación en estado "${FILTER_LABELS[activeFilter] || activeFilter}". Los que ya tienen cuenta se marcan y no pueden volver a invitarse.`
            : `${rows.length} intérprete(s) en el directorio que aún no tienen una cuenta de usuario. Selecciona y envíales invitaciones por correo para que registren su contraseña. Descarga el Excel para el visto bueno del consejo antes de enviar.`}
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={onDownloadAll} disabled={rows.length === 0}
            data-testid="orphans-export-all"
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-cmic-200 text-cmic-700 text-sm font-medium rounded-lg hover:bg-cmic-50 disabled:opacity-50">
            <Download size={14} /> Excel (todos)
          </button>
          <button onClick={onDownloadSelected} disabled={selected.size === 0}
            data-testid="orphans-export-selected"
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-cmic-200 text-cmic-700 text-sm font-medium rounded-lg hover:bg-cmic-50 disabled:opacity-50">
            <Download size={14} /> Excel ({selected.size})
          </button>
          <button onClick={onBulkInvite} disabled={selected.size === 0 || inviting}
            data-testid="orphans-bulk-invite"
            className="inline-flex items-center gap-2 px-4 py-2 bg-cmic-600 text-white text-sm font-medium rounded-lg hover:bg-cmic-700 disabled:opacity-50">
            {inviting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Enviar invitación ({selected.size})
          </button>
          <button onClick={onBulkRemind} disabled={selected.size === 0 || inviting}
            data-testid="orphans-bulk-remind"
            title="Reenvía un recordatorio a los seleccionados. Si su invitación expiró, se genera una nueva."
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50">
            {inviting ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />}
            Recordar seleccionados ({selected.size})
          </button>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-3 w-10 text-center">
                  <input type="checkbox" checked={allChecked} onChange={(e) => onSelectAll(e.target.checked)}
                    data-testid="orphans-select-all" className="accent-cmic-600" />
                </th>
                <th className="text-right px-2 py-3 font-medium text-charcoal-400 text-[10px] w-10">#</th>
                <SortTh label="Intérprete" k="name" />
                <SortTh label="Correo" k="email" />
                <SortTh label="Invitación" k="invitation" />
                <SortTh label="Abierto" k="opened" />
                <SortTh label="Categoría" k="category" />
                <SortTh label="Ciudad" k="city" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedRows.map((i, idx) => (
                <tr key={i.slug} data-testid={`orphan-row-${i.slug}`}
                  className={selected.has(i.slug) ? 'bg-cmic-50/40' : 'hover:bg-gray-50/60'}>
                  <td className="px-3 py-3 text-center">
                    <input type="checkbox" checked={selected.has(i.slug)} onChange={() => onToggle(i.slug)}
                      disabled={i.has_account}
                      title={i.has_account ? 'Ya tiene cuenta' : undefined}
                      data-testid={`orphan-checkbox-${i.slug}`} className="accent-cmic-600 disabled:opacity-30" />
                  </td>
                  <td className="px-2 py-3 text-right text-charcoal-400 text-xs tabular-nums">{idx + 1}</td>
                  <td className="px-3 py-3 font-medium text-charcoal-800">
                    {i.full_name}
                    {i.has_account && <span className="ml-2 text-[9px] font-bold uppercase text-emerald-600 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5">tiene cuenta</span>}
                  </td>
                  <td className="px-3 py-3 text-charcoal-700">{i.email || <span className="text-charcoal-400 italic">sin correo</span>}</td>
                  <td className="px-3 py-3" data-testid={`orphan-invite-status-${i.slug}`}>
                    {(() => {
                      const inv = invMap?.[i.slug];
                      const st = getInviteStatus(inv);
                      const fmt = (d) => { try { return new Date(d).toLocaleDateString('es-MX'); } catch { return ''; } };
                      return (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] font-semibold border rounded px-2 py-0.5 whitespace-nowrap ${st.cls}`}>{st.label}</span>
                            {inv && inv.token && <CopyLinkButton token={inv.token} testid={`orphan-invite-copy-${i.slug}`} label="Enlace" />}
                          </div>
                          {inv && inv.accepted_at && (
                            <span className="text-[10px] text-emerald-600">Activado: {fmt(inv.accepted_at)}</span>
                          )}
                          {inv && (inv.reminder_count > 0) && (
                            <span className={`text-[10px] ${inv.reminder_count >= 3 ? 'text-rose-600 font-semibold' : 'text-amber-600'}`} data-testid={`orphan-reminders-${i.slug}`}>
                              {inv.reminder_count} recordatorio{inv.reminder_count !== 1 ? 's' : ''}{inv.reminder_sent_at ? ` · últ.: ${fmt(inv.reminder_sent_at)}` : ''}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-3 text-xs" data-testid={`orphan-opened-at-${i.slug}`}>
                    {(() => {
                      const inv = invMap?.[i.slug];
                      const fmt = (d) => { try { return new Date(d).toLocaleDateString('es-MX'); } catch { return ''; } };
                      return inv && inv.opened_at
                        ? <span className="text-sky-600 font-medium whitespace-nowrap">{fmt(inv.opened_at)}</span>
                        : <span className="text-charcoal-300">—</span>;
                    })()}
                  </td>
                  <td className="px-3 py-3 text-xs text-charcoal-500 capitalize">{i.membership_status || '—'}</td>
                  <td className="px-3 py-3 text-charcoal-500">
                    <div className="flex items-center justify-between gap-2">
                      <span>{i.city || '—'}</span>
                      <button onClick={() => onHide(i)} data-testid={`orphan-hide-${i.slug}`}
                        title="Ocultar de estadísticas y listas (no se borra; se puede restaurar)"
                        className="text-charcoal-300 hover:text-rose-600 hover:bg-rose-50 rounded p-1">
                        <EyeOff size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan="8" className="px-4 py-10 text-center text-charcoal-500 text-sm">Todos los intérpretes tienen cuenta 🎉</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved, onDeleted }) {
  const { user: actor } = useAuth();
  const isSuperAdmin = actor?.role === 'admin';
  const [tab, setTab] = useState('profile');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Role + link state (loaded on demand)
  const [role, setRole] = useState(user.role || 'interpreter');
  const [savingRole, setSavingRole] = useState(false);
  const [modPerms, setModPerms] = useState(() => initialMods(user));
  const [savingMod, setSavingMod] = useState(false);
  const [linkSlug, setLinkSlug] = useState(user.interpreter_slug || '');
  const [savingLink, setSavingLink] = useState(false);
  const [allInterpreters, setAllInterpreters] = useState([]);
  const [interpSearch, setInterpSearch] = useState('');
  const [interpLoading, setInterpLoading] = useState(false);
  const [form, setForm] = useState({
    username:      user.username || '',
    full_name:     user.full_name || '',
    email:         user.email || '',
    is_active:     user.is_active !== false,
    forum_access:  user.forum_access !== false,
    forum_permission: user.forum_permission || 'full',
    events_access: user.events_access !== false,
    new_password:  '',
    confirm_password: '',
    force_password_change: true,
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState('');
  const [savedPw, setSavedPw] = useState('');
  const [copiedPw, setCopiedPw] = useState(false);

  async function copyPassword() {
    try {
      await navigator.clipboard.writeText(savedPw);
      setCopiedPw(true);
      setTimeout(() => setCopiedPw(false), 2500);
    } catch {
      setErr('No se pudo copiar automáticamente. Cópiala manualmente.');
    }
  }

  async function handleDelete() {
    const hasInterp = !!user.interpreter;
    const msg = hasInterp
      ? `¿Mover a la papelera a "${user.full_name || user.username}" y su perfil de intérprete? Desaparecerá por completo del sitio (directorio, búsqueda, inicio de sesión), pero podrás restaurarlo desde la pestaña Papelera.`
      : `¿Mover a la papelera a "${user.full_name || user.username}"? Desaparecerá del sitio, pero podrás restaurarlo desde la pestaña Papelera.`;
    if (!window.confirm(msg)) return;
    setDeleting(true); setErr('');
    try {
      await api.delete(`/admin/community-users/${user.id}`);
      onDeleted();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || 'Error al borrar');
      setDeleting(false);
    }
  }

  useEffect(() => {
    if (tab !== 'history') return;
    setHistoryLoading(true);
    api.get(`/admin/community-users/${user.id}/event-history`)
      .then((r) => setHistory(r.data || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [tab, user.id]);

  // Load all interpreters once (only when the "link" tab opens) for the search list.
  useEffect(() => {
    if (tab !== 'link' || allInterpreters.length > 0) return;
    setInterpLoading(true);
    api.get('/admin/community-users/all-interpreters')
      .then((r) => setAllInterpreters(r.data || []))
      .catch(() => setAllInterpreters([]))
      .finally(() => setInterpLoading(false));
  }, [tab, allInterpreters.length]);

  const filteredInterpreters = useMemo(() => {
    const q = norm(interpSearch.trim());
    const list = allInterpreters;
    if (!q) return list.slice(0, 50);
    return list.filter((i) => (
      norm(i.full_name).includes(q) ||
      norm(i.email).includes(q) ||
      norm(i.slug).includes(q)
    )).slice(0, 100);
  }, [allInterpreters, interpSearch]);

  function set(k) { return (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value })); }

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      const payload = {
        username: form.username,
        full_name: form.full_name,
        email: form.email,
        is_active: form.is_active,
        forum_access: form.forum_access,
        forum_permission: form.forum_permission,
        events_access: form.events_access,
      };
      if (form.new_password) {
        if (form.new_password.length < 8) {
          setErr('La contraseña debe tener al menos 8 caracteres.');
          setSaving(false);
          return;
        }
        if (form.new_password !== form.confirm_password) {
          setErr('Las contraseñas no coinciden. Verifica el campo "Confirmar contraseña".');
          setSaving(false);
          return;
        }
        payload.new_password = form.new_password;
        payload.force_password_change = !!form.force_password_change;
      }
      await api.put(`/admin/community-users/${user.id}`, payload);
      if (form.new_password) {
        setSavedPw(form.new_password);
        setForm((f) => ({ ...f, new_password: '', confirm_password: '' }));
      } else {
        onSaved();
      }
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || 'Error');
    } finally { setSaving(false); }
  }

  async function saveRole() {
    setSavingRole(true); setErr('');
    try {
      await api.put(`/admin/community-users/${user.id}/role`, { role });
      onSaved();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || 'Error');
    } finally { setSavingRole(false); }
  }

  function toggleMod(k) {
    setModPerms((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
  }

  async function saveModPerms() {
    setSavingMod(true); setErr('');
    try {
      await api.put(`/admin/community-users/${user.id}`, { forum_mod_permissions: modPerms });
      onSaved();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || 'Error');
    } finally { setSavingMod(false); }
  }

  async function saveLink(slug) {
    setSavingLink(true); setErr('');
    try {
      await api.put(`/admin/community-users/${user.id}/link`, { interpreter_slug: slug || null });
      setLinkSlug(slug || '');
      onSaved();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || 'Error');
    } finally { setSavingLink(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="edit-community-user-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h2 className="font-bold text-charcoal-800 text-base flex items-center gap-2"><UserPlus size={18} className="text-cmic-600" /> Editar usuario</h2>
            <p className="text-[11px] text-charcoal-500 mt-0.5">
              {user.interpreter ? (
                <>Sincronizado con intérprete <strong>{user.interpreter.full_name}</strong>. Los cambios en nombre/correo actualizan ambos.</>
              ) : (
                'Usuario sin perfil de intérprete vinculado.'
              )}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100"><X size={16} /></button>
        </div>
        <div className="flex gap-1 px-5 pt-3 border-b border-gray-200">
          <button type="button" onClick={() => setTab('profile')}
            data-testid="edit-tab-profile"
            className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px ${tab === 'profile' ? 'border-cmic-600 text-cmic-700' : 'border-transparent text-charcoal-500 hover:text-charcoal-800'}`}>
            Perfil
          </button>
          <button type="button" onClick={() => setTab('link')}
            data-testid="edit-tab-link"
            className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px ${tab === 'link' ? 'border-cmic-600 text-cmic-700' : 'border-transparent text-charcoal-500 hover:text-charcoal-800'}`}>
            Vinculación
          </button>
          <button type="button" onClick={() => setTab('forum')}
            data-testid="edit-tab-forum"
            className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px ${tab === 'forum' ? 'border-cmic-600 text-cmic-700' : 'border-transparent text-charcoal-500 hover:text-charcoal-800'}`}>
            Permisos del foro
          </button>
          <button type="button" onClick={() => setTab('history')}
            data-testid="edit-tab-history"
            className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px ${tab === 'history' ? 'border-cmic-600 text-cmic-700' : 'border-transparent text-charcoal-500 hover:text-charcoal-800'}`}>
            Histórico de eventos
          </button>
        </div>
        {tab === 'link' ? (
          <div className="p-5 overflow-y-auto" data-testid="user-link-section">
            {err && <div className="mb-3 bg-red-50 border border-red-200 text-red-700 rounded-lg p-2 text-xs">{err}</div>}

            {/* Role section ────────────────────────────────────────── */}
            <div className="mb-6">
              <p className="text-xs font-semibold text-charcoal-700 mb-2 uppercase tracking-wider flex items-center gap-1">
                <ShieldCheck size={12} className="text-cmic-600" /> Rol del usuario
              </p>
              {isSuperAdmin ? (
                <div className="flex items-center gap-2">
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    data-testid="edit-role-select"
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm flex-1"
                  >
                    <option value="interpreter">Intérprete (acceso al portal)</option>
                    <option value="council">Consejo (admin con permisos limitados)</option>
                    <option value="admin">Administrador (acceso total)</option>
                  </select>
                  <button
                    type="button"
                    onClick={saveRole}
                    disabled={savingRole || role === user.role}
                    data-testid="edit-role-save"
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-cmic-600 text-white text-xs font-medium hover:bg-cmic-700 disabled:opacity-50"
                  >
                    {savingRole ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Cambiar rol
                  </button>
                </div>
              ) : (
                <div className="text-xs text-charcoal-500">
                  Rol actual: <strong className="text-charcoal-800 capitalize">{user.role}</strong>.
                  Solo el super-administrador puede cambiar el rol de un usuario.
                </div>
              )}
              <p className="text-[11px] text-charcoal-500 mt-1.5">
                Cambiar a <em>consejo</em> hará que el usuario desaparezca de esta lista y aparezca en <Link to="/admin/users" className="text-cmic-600 hover:underline">Equipo CMIC</Link>.
              </p>
            </div>

            {/* Interpreter link section ──────────────────────────── */}
            <div>
              <p className="text-xs font-semibold text-charcoal-700 mb-2 uppercase tracking-wider flex items-center gap-1">
                <Link2 size={12} className="text-cmic-600" /> Perfil de intérprete vinculado
              </p>
              {linkSlug ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3 flex items-center justify-between gap-3" data-testid="user-current-link">
                  <div className="text-sm">
                    <span className="text-emerald-700 font-medium">Vinculado a:</span>{' '}
                    <strong className="text-charcoal-800">{user.interpreter?.full_name || linkSlug}</strong>
                    <div className="text-[10px] text-charcoal-500 mt-0.5">slug: <code>{linkSlug}</code></div>
                  </div>
                  <button
                    type="button"
                    onClick={() => saveLink('')}
                    disabled={savingLink}
                    data-testid="edit-link-unlink"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-rose-50 text-rose-700 border border-rose-200 text-xs font-medium hover:bg-rose-100 disabled:opacity-50"
                  >
                    {savingLink ? <Loader2 size={12} className="animate-spin" /> : <Link2Off size={12} />} Desvincular
                  </button>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 text-sm text-charcoal-500">
                  Esta cuenta no está vinculada a ningún perfil de intérprete.
                </div>
              )}
              <div className="relative mb-2">
                <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />
                <input
                  type="text"
                  value={interpSearch}
                  onChange={(e) => setInterpSearch(e.target.value)}
                  placeholder="Buscar intérprete por nombre, correo o slug…"
                  data-testid="edit-link-search"
                  className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
                {interpLoading ? (
                  <div className="py-6 flex items-center justify-center"><Loader2 className="animate-spin text-cmic-500" size={20} /></div>
                ) : filteredInterpreters.length === 0 ? (
                  <div className="py-6 text-center text-sm text-charcoal-400 italic">Sin resultados.</div>
                ) : (
                  filteredInterpreters.map((i) => {
                    const isCurrent = i.slug === linkSlug;
                    const otherUserLinked = !!i.already_linked && !isCurrent;
                    return (
                      <button
                        key={i.slug}
                        type="button"
                        onClick={() => !otherUserLinked && !isCurrent && saveLink(i.slug)}
                        disabled={otherUserLinked || isCurrent || savingLink}
                        data-testid={`edit-link-row-${i.slug}`}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 border-b border-gray-100 text-left text-sm transition-colors ${
                          isCurrent ? 'bg-emerald-50/60' : otherUserLinked ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:bg-cmic-50'
                        }`}
                      >
                        {i.photo_url ? (
                          <img src={i.photo_url} alt="" className="w-8 h-8 rounded-full object-cover bg-gray-100" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-100 text-charcoal-400 flex items-center justify-center text-[10px] font-bold uppercase">
                            {(i.full_name || '?').charAt(0)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-charcoal-800 truncate">{i.full_name}</div>
                          <div className="text-[10px] text-charcoal-500 truncate">{i.email || 'sin correo'} · {i.membership_status || '—'}</div>
                        </div>
                        {isCurrent && (
                          <span className="text-[10px] font-bold text-emerald-700 uppercase">Actual</span>
                        )}
                        {otherUserLinked && (
                          <span className="text-[10px] font-bold text-rose-600 uppercase">Ocupado</span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-charcoal-600 hover:bg-gray-100 text-sm">Cerrar</button>
            </div>
          </div>
        ) : tab === 'forum' ? (
          <div className="p-5 overflow-y-auto" data-testid="user-forum-perms-section">
            {err && <div className="mb-3 bg-red-50 border border-red-200 text-red-700 rounded-lg p-2 text-xs">{err}</div>}
            <p className="text-xs font-semibold text-charcoal-700 mb-1 uppercase tracking-wider flex items-center gap-1">
              <ShieldCheck size={12} className="text-cmic-600" /> Permisos de moderación del foro
            </p>
            <p className="text-[11px] text-charcoal-500 mb-4">
              Define qué acciones de moderación puede realizar este usuario en el foro. Pensado para miembros del Consejo.
            </p>
            {user.role === 'admin' ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-center gap-2" data-testid="forum-perms-admin-note">
                <ShieldCheck size={16} /> El super-administrador siempre tiene todos los permisos de moderación.
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  {FORUM_MOD_OPTIONS.map((opt) => (
                    <label key={opt.key} data-testid={`forum-perm-${opt.key}`}
                      className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={modPerms.includes(opt.key)} onChange={() => toggleMod(opt.key)}
                        data-testid={`forum-perm-checkbox-${opt.key}`} className="mt-0.5 accent-cmic-600 w-4 h-4" />
                      <span>
                        <span className="block text-sm font-medium text-charcoal-800">{opt.label}</span>
                        <span className="block text-[11px] text-charcoal-500">{opt.desc}</span>
                      </span>
                    </label>
                  ))}
                </div>
                <div className="flex justify-end mt-4">
                  <button type="button" onClick={saveModPerms} disabled={savingMod} data-testid="forum-perms-save"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cmic-600 text-white text-sm font-medium hover:bg-cmic-700 disabled:opacity-50">
                    {savingMod ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar permisos
                  </button>
                </div>
              </>
            )}
          </div>
        ) : tab === 'history' ? (
          <div className="p-5 overflow-y-auto" data-testid="user-event-history">
            {historyLoading ? (
              <div className="py-10 flex items-center justify-center"><Loader2 className="animate-spin text-cmic-500" size={24} /></div>
            ) : history.length === 0 ? (
              <p className="text-charcoal-500 text-sm text-center py-10">Este usuario no ha registrado a ningún evento todavía.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-right px-2 py-2 font-medium text-charcoal-400 text-[10px] w-10">#</th>
                    <th className="text-left px-3 py-2 font-medium text-charcoal-600">Evento</th>
                    <th className="text-left px-3 py-2 font-medium text-charcoal-600">Fecha</th>
                    <th className="text-center px-3 py-2 font-medium text-charcoal-600">Asistencia</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {history.map((h, idx) => (
                    <tr key={h.id} data-testid={`history-row-${h.event_id}`}>
                      <td className="px-2 py-2 text-right text-charcoal-400 text-xs tabular-nums">{idx + 1}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-charcoal-800">{h.event?.title || '(evento eliminado)'}</div>
                        <div className="text-[10px] text-charcoal-500">Registrado el {new Date(h.registered_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                      </td>
                      <td className="px-3 py-2 text-[11px] text-charcoal-500">{h.event?.event_date ? new Date(h.event.event_date).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                      <td className="px-3 py-2 text-center">
                        {h.attended ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded">ASISTIÓ</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-gray-100 text-charcoal-500 px-2 py-0.5 rounded">SIN ASISTENCIA</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="flex justify-end mt-4">
              <button onClick={onClose} className="px-4 py-2 rounded-lg text-charcoal-600 hover:bg-gray-100 text-sm">Cerrar</button>
            </div>
          </div>
        ) : (
        <form onSubmit={submit} className="p-5 space-y-4 text-sm overflow-y-auto">
          {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-2 text-xs">{err}</div>}
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Nombre completo"><input value={form.full_name} onChange={set('full_name')} data-testid="edit-fullname" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="Usuario (username)"><input value={form.username} onChange={set('username')} data-testid="edit-username" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" /></Field>
          </div>
          <Field label="Correo electrónico"><input type="email" value={form.email} onChange={set('email')} data-testid="edit-email" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" /></Field>

          <div className="border-t border-gray-100 pt-3">
            <p className="text-xs font-semibold text-charcoal-700 mb-2 uppercase tracking-wider">Accesos</p>
            <label className="flex items-start gap-3 mb-2 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={set('is_active')} data-testid="edit-active" className="mt-0.5 accent-cmic-600" />
              <div>
                <div className="font-medium text-charcoal-800">Cuenta activa</div>
                <div className="text-[11px] text-charcoal-500">Si está desactivado, no podrá iniciar sesión.</div>
              </div>
            </label>
            <label className="flex items-start gap-3 mb-2 cursor-pointer">
              <input type="checkbox" checked={form.forum_access} onChange={set('forum_access')} data-testid="edit-forum-access" className="mt-0.5 accent-cmic-600" />
              <div>
                <div className="font-medium text-charcoal-800">Acceso al área Interna</div>
                <div className="text-[11px] text-charcoal-500">Permite ver el foro y contenido privado para miembros activos.</div>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.events_access} onChange={set('events_access')} data-testid="edit-events-access" className="mt-0.5 accent-cmic-600" />
              <div>
                <div className="font-medium text-charcoal-800">Acceso a Eventos internos</div>
                <div className="text-[11px] text-charcoal-500">Permite ver y registrarse a los eventos privados.</div>
              </div>
            </label>
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="font-medium text-charcoal-800 mb-1">Permisos del Foro</div>
              <div className="text-[11px] text-charcoal-500 mb-2">Controla qué puede hacer este usuario en el foro. Útil para moderar usuarios conflictivos.</div>
              <select
                value={form.forum_permission}
                onChange={set('forum_permission')}
                data-testid="edit-forum-permission"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30"
              >
                <option value="full">Acceso completo — ver, crear temas y responder</option>
                <option value="reply">Ver y responder — no puede crear temas nuevos</option>
                <option value="read">Solo lectura — puede ver pero no publicar</option>
                <option value="blocked">Bloqueado — sin acceso al foro</option>
              </select>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3">
            <p className="text-xs font-semibold text-charcoal-700 mb-2 uppercase tracking-wider flex items-center gap-1"><KeyRound size={12} /> Cambiar contraseña</p>
            {savedPw ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="edit-password-result">
                <p className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5 mb-2"><Check size={15} /> Contraseña establecida</p>
                <p className="text-[11px] text-emerald-700 mb-3">Cópiala y compártela con el usuario por un canal seguro. No volverá a mostrarse.</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white border border-emerald-200 rounded-lg px-3 py-2 text-sm font-mono text-charcoal-800 break-all" data-testid="edit-password-value">{savedPw}</code>
                  <button type="button" onClick={copyPassword} data-testid="edit-password-copy"
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap ${copiedPw ? 'bg-emerald-600 text-white' : 'bg-cmic-600 hover:bg-cmic-700 text-white'}`}>
                    {copiedPw ? <><Check size={14} /> Copiada</> : <><Copy size={14} /> Copiar</>}
                  </button>
                </div>
                <button type="button" onClick={() => { setSavedPw(''); setCopiedPw(false); }} data-testid="edit-password-again"
                  className="mt-3 text-xs text-charcoal-500 hover:text-cmic-600 font-medium">Establecer otra contraseña</button>
              </div>
            ) : (
            <>
            <input type="password" value={form.new_password} onChange={set('new_password')}
              placeholder="Nueva contraseña (mínimo 8 caracteres)"
              data-testid="edit-password"
              autoComplete="new-password"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            <input type="password" value={form.confirm_password} onChange={set('confirm_password')}
              placeholder="Confirmar nueva contraseña"
              data-testid="edit-password-confirm"
              autoComplete="new-password"
              disabled={!form.new_password}
              className={`mt-2 w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-50 disabled:cursor-not-allowed ${
                form.new_password && form.confirm_password && form.new_password !== form.confirm_password
                  ? 'border-rose-300 bg-rose-50/30'
                  : 'border-gray-200'
              }`} />
            {form.new_password && form.confirm_password && form.new_password !== form.confirm_password && (
              <p className="text-[11px] text-rose-600 mt-1">Las contraseñas no coinciden.</p>
            )}
            <label className={`mt-2.5 flex items-start gap-2 text-xs ${!form.new_password ? 'opacity-50' : ''}`}>
              <input
                type="checkbox"
                checked={!!form.force_password_change}
                onChange={set('force_password_change')}
                disabled={!form.new_password}
                data-testid="edit-force-pw-change"
                className="mt-0.5 w-4 h-4 accent-cmic-600"
              />
              <span className="text-charcoal-700">
                <strong>Forzar cambio en el próximo inicio de sesión.</strong>
                <span className="block text-[11px] text-charcoal-500 mt-0.5">Si está activo, al ingresar con esta contraseña el usuario será llevado a la página de cambio de contraseña y no podrá usar el portal hasta que defina la suya.</span>
              </span>
            </label>
            <p className="text-[11px] text-charcoal-500 mt-2">Déjalo en blanco para no cambiar la contraseña.</p>
            </>
            )}
          </div>

          <div className="flex justify-between items-center gap-2 pt-2 border-t border-gray-100 mt-2">
            <button type="button" onClick={handleDelete} disabled={deleting || saving} data-testid="edit-delete-user"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-rose-600 hover:bg-rose-50 border border-rose-200 font-medium text-sm disabled:opacity-50">
              {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Borrar usuario
            </button>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-charcoal-600 hover:bg-gray-100 text-sm">Cancelar</button>
              <button type="submit" disabled={saving} data-testid="edit-save"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cmic-600 text-white font-medium text-sm hover:bg-cmic-700 disabled:opacity-50">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
              </button>
            </div>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (<label className="block"><span className="block text-xs font-medium text-charcoal-600 mb-1">{label}</span>{children}</label>);
}
